import { z } from 'zod/v4';
import { creditorMessageRecipientsSchema } from '../../interactions/schemas.js';
import { ToolCategory, ToolName, ToolPolicy, ToolWeight } from '../types.js';
const recipientIdsSchema = creditorMessageRecipientsSchema;
const inputSchema = z.object({
    body: z
        .string()
        .min(1)
        .max(20_000)
        .describe([
        'HTML message shown to the creditor in the creditor portal.',
        "Write in the creditor contact's preferred language. Search creditor-message templates and mentions first.",
        'Use a warm, friendly, familiar, and collaborative professional tone. Creditors are ongoing customer relationships, so this should feel less formal and transactional than debtor-facing communication.',
        'Never add a manual signature unless the matching tenant template contains one.',
    ].join(' ')),
    templateId: z
        .uuid()
        .optional()
        .describe('Optional CREDITOR_MESSAGE template used as provenance and for mention hydration. Obtain via search_templates.'),
    recipientIds: recipientIdsSchema
        .optional()
        .describe('Creditor client-user ids to notify by email. Usually omit this so the creditor contact is notified. An explicit empty array sends no notification; the message is still visible in the creditor portal. The caseworker can edit recipients before approval.'),
    attachments: z
        .array(z.object({
        fileId: z
            .uuid()
            .describe('Existing file to attach. Obtain via search_documents, get_interaction.attachments, or other tools that return file ids.'),
    }))
        .optional()
        .describe('Files to make available with the creditor message. Only existing files on this case can be attached by the agent; the reviewing caseworker can also upload files before approval.'),
    reasoning: z
        .string()
        .max(500)
        .optional()
        .describe('Why this creditor message is necessary. Short rationale visible to the reviewing caseworker.'),
});
const outputSchema = z.object({
    interactionId: z.uuid(),
});
const editSchema = z.object({
    body: z.string().min(1).max(20_000).optional(),
    recipientIds: recipientIdsSchema.optional(),
    attachments: z.array(z.object({ fileId: z.uuid() })).optional(),
});
export const createCreditorMessageTool = {
    name: ToolName.CreateCreditorMessage,
    category: ToolCategory.Interactions,
    weight: ToolWeight.Heavy,
    version: 1,
    defaultPolicy: ToolPolicy.RequireApproval,
    llmDescription: 'Propose an HTML message to the creditor through the creditor portal. This is not an email interaction: it is visible in the creditor portal and may notify selected creditor users by email. HEAVY: calling this ends the run; a caseworker reviews and may edit the body, recipients, and attachments before approval. Use a warm, friendly, familiar, and collaborative professional tone—creditors are ongoing customer relationships, so creditor messages should feel less formal and transactional than debtor messages. Use it when the creditor must provide information, documentation, or a decision. Existing files from this case can be attached when useful. Read get_creditor, search creditor-message templates, and use live {{mentions}} instead of inlining mentionable case values. Do not also create an awaiting-creditor update: sending the message creates that state automatically.',
    inputSchema,
    outputSchema,
    editSchema,
    previewable: true,
    summarizeProposal: () => 'Send creditor message',
};
//# sourceMappingURL=create-creditor-message.js.map